import { useState } from "react"
import {
  KeyRoundIcon,
  Loader2,
  LogOutIcon,
  MoreVerticalIcon,
} from "lucide-react"

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "./ui/avatar"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog"
import { Button } from "./ui/button"
import { Input } from "./ui/input"
import { Label } from "./ui/label"
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "./ui/sidebar"
import { useChangeOwnPassword, usePasswordPolicy } from "../lib/api-central-mu"
import { useToast } from "../lib/use-toast"
import { encodeCredential } from "../utils/encryption"
import { PasswordPolicyChecklist, passwordSatisfiesPolicy } from "./PasswordPolicyChecklist"

export function NavUser({
  user,
  onLogout,
}: {
  user: {
    name: string
    email: string
    avatar: string
  }
  onLogout?: () => void
}) {
  const { isMobile } = useSidebar()
  const [pwOpen, setPwOpen] = useState(false)
  const initials = user.name.split(' ').map(n => n[0]).join('').toUpperCase()

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        {/* modal={false}: "Change password" opens a dialog while this menu is still open
            (onSelect preventDefault) — two modal layers race the body pointer-events restore
            and freeze the page until refresh (radix-ui/primitives#1836). */}
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <Avatar className="h-8 w-8 rounded-lg grayscale">
                <AvatarImage src={user.avatar} alt={user.name} />
                <AvatarFallback className="rounded-lg">{initials}</AvatarFallback>
              </Avatar>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-medium">{user.name}</span>
              </div>
              <MoreVerticalIcon className="ml-auto size-4" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
            side={isMobile ? "bottom" : "right"}
            align="end"
            sideOffset={4}
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                <Avatar className="h-8 w-8 rounded-lg">
                  <AvatarImage src={user.avatar} alt={user.name} />
                  <AvatarFallback className="rounded-lg">{initials}</AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-medium">{user.name}</span>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => setPwOpen(true)}>
              <KeyRoundIcon />
              Change password
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onLogout}>
              <LogOutIcon />
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>

      <Dialog open={pwOpen} onOpenChange={setPwOpen}>
        {pwOpen ? <ChangePasswordDialog onDone={() => setPwOpen(false)} /> : null}
      </Dialog>
    </SidebarMenu>
  )
}

function ChangePasswordDialog({ onDone }: { onDone: () => void }) {
  const [current, setCurrent] = useState("")
  const [next, setNext] = useState("")
  const [confirm, setConfirm] = useState("")
  const { mutate, isPending } = useChangeOwnPassword()
  const { data: policy } = usePasswordPolicy()
  const { toast } = useToast()

  // Gate on the live policy when it loaded; if the fetch failed, fall back to non-empty and
  // let the server enforce the policy.
  const policyOk = policy ? passwordSatisfiesPolicy(policy, next) : next.trim().length > 0
  const confirmOk = next.length > 0 && next === confirm
  const canSubmit = current.trim().length > 0 && policyOk && confirmOk && !isPending

  const submit = () => {
    if (!canSubmit) return
    mutate(
      { currentPassword: encodeCredential(current), newPassword: encodeCredential(next) },
      {
        onSuccess: () => {
          toast({ title: "Password changed" })
          onDone()
        },
      }
    )
  }

  return (
    <DialogContent className="sm:max-w-md">
      <DialogHeader>
        <DialogTitle>Change password</DialogTitle>
        <DialogDescription>Update the password you use to sign in to the Migration Utility.</DialogDescription>
      </DialogHeader>
      <div className="space-y-3">
        <div className="space-y-2">
          <Label htmlFor="cur-pw">Current password</Label>
          <Input id="cur-pw" type="password" value={current} onChange={(e) => setCurrent(e.target.value)} autoComplete="current-password" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="new-pw">New password</Label>
          <Input id="new-pw" type="password" value={next} onChange={(e) => setNext(e.target.value)} autoComplete="new-password" />
          {policy ? <PasswordPolicyChecklist policy={policy} password={next} className="pt-1" /> : null}
        </div>
        <div className="space-y-2">
          <Label htmlFor="confirm-pw">Confirm new password</Label>
          <Input id="confirm-pw" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" />
          {confirm.length > 0 && !confirmOk ? (
            <p className="text-xs text-destructive">New passwords don't match.</p>
          ) : null}
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onDone}>Cancel</Button>
        <Button onClick={submit} disabled={!canSubmit}>
          {isPending ? <Loader2 className="size-4 animate-spin" /> : null}
          Change password
        </Button>
      </DialogFooter>
    </DialogContent>
  )
}